/**
 * @fileoverview Dammen Board Component
 * @description Renders the 8x8 checkerboard with pieces
 */

import React, { memo, useMemo, type ReactElement } from "react";
import { View, StyleSheet, Dimensions } from "react-native";
import { DammenCellComponent } from "./DammenCell";
import { DammenBoard as BoardType, BoardPosition, Move, PlayerId } from "../types";

interface DammenBoardProps {
  board: BoardType;
  selectedPosition: BoardPosition | null;
  validMoves: Move[];
  mustJumpFrom: BoardPosition | null;
  myPiece: PlayerId | null;
  isMyTurn: boolean;
  onCellPress: (position: BoardPosition) => void;
  disabled?: boolean;
}

const BOARD_PADDING = 8;

/**
 * Dammen Board Component
 * Renders the 8x8 checkerboard
 */
export const DammenBoard: React.FC<DammenBoardProps> = memo(
  ({
    board,
    selectedPosition,
    validMoves,
    mustJumpFrom,
    myPiece,
    isMyTurn,
    onCellPress,
    disabled = false,
  }) => {
    // Calculate cell size based on screen width
    const screenWidth = Dimensions.get("window").width;
    const boardSize = Math.min(screenWidth - 32, 400);
    const cellSize = (boardSize - BOARD_PADDING * 2) / 8;

    // Create set of valid move destinations for quick lookup
    const validMovePositions = useMemo(() => {
      const positions = new Set<BoardPosition>();
      for (const move of validMoves) {
        positions.add(move.to);
      }
      return positions;
    }, [validMoves]);

    // Create set of capture positions
    const capturePositions = useMemo(() => {
      const positions = new Set<BoardPosition>();
      for (const move of validMoves) {
        if (move.isJump) {
          positions.add(move.to);
        }
      }
      return positions;
    }, [validMoves]);

    // Render the board as rows
    const rows = useMemo(() => {
      const result: ReactElement[] = [];

      for (let row = 0; row < 8; row++) {
        const cells: ReactElement[] = [];

        for (let col = 0; col < 8; col++) {
          const position = row * 8 + col;
          const cell = board[position];

          cells.push(
            <DammenCellComponent
              key={position}
              position={position}
              cell={cell}
              cellSize={cellSize}
              isSelected={selectedPosition === position}
              isValidMove={validMovePositions.has(position)}
              isValidCapture={capturePositions.has(position)}
              isMustJump={mustJumpFrom === position}
              onPress={onCellPress}
              disabled={disabled || !isMyTurn}
            />
          );
        }

        result.push(
          <View key={row} style={styles.row}>
            {cells}
          </View>
        );
      }

      return result;
    }, [
      board,
      cellSize,
      selectedPosition,
      validMovePositions,
      capturePositions,
      mustJumpFrom,
      onCellPress,
      disabled,
      isMyTurn,
    ]);

    return (
      <View
        style={[
          styles.container,
          {
            width: boardSize,
            height: boardSize,
            padding: BOARD_PADDING,
          },
        ]}
      >
        <View style={styles.board}>{rows}</View>
      </View>
    );
  }
);

DammenBoard.displayName = "DammenBoard";

const styles = StyleSheet.create({
  container: {
    backgroundColor: "#5D3A1A",
    borderRadius: 8,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  board: {
    flex: 1,
    overflow: "hidden",
    borderRadius: 4,
  },
  row: {
    flexDirection: "row",
  },
});

